'use client'

/**
 * Hook to get the current user's role for the active property.
 * Returns role_key from property_members and helper booleans.
 *
 * Uses the existing GetUserRoleForProperty GraphQL query (menu.graphql).
 */

import { useQuery } from '@apollo/client/react'
import { useAuth } from '@/contexts/AuthContext'
import { useOptionalSelection } from '@/contexts/SelectionContext'
import { GetUserRoleForPropertyDocument } from '@/graphql/menu.generated'
import { FETCH_POLICIES, POLL_INTERVALS } from '@/lib/apollo-policies'
import { useActiveRoleOverride } from './useActiveRoleOverride'

/** Admin-level roles that bypass restrictions */
const ADMIN_ROLES = new Set(['owner', 'tenant_admin', 'admin', 'manager', 'property_admin'])

export interface PropertyRole {
  /** Raw role key from property_members (e.g., 'front_desk', 'tenant_admin', 'owner') */
  roleKey: string | null
  /** True for owner, tenant_admin, admin roles */
  isAdmin: boolean
  /** True while the query is loading */
  loading: boolean
}

export function usePropertyRole(): PropertyRole {
  const { user } = useAuth()
  // Optional: no SelectionProvider outside the dashboard layout (growth/agency
  // portals). Without one there is no active property, so the query skips and the
  // role falls back to the override / null — see useOptionalSelection.
  const selectedPropertyId = useOptionalSelection()?.selectedPropertyId ?? null

  // Per-tab role override (user-menu role switcher): the UI must gate on the
  // same role the requests run as (lib/active-role attaches it as
  // x-hasura-role), otherwise "acting as tenant_admin" would hide buttons the
  // data layer actually permits — or vice versa. Read through the hook so it
  // is null on the first render (hydration parity with the SSR HTML, which
  // cannot see sessionStorage).
  const roleOverride = useActiveRoleOverride()

  const userId = user?.id
  const skip = !userId || !selectedPropertyId || !!roleOverride

  /*
    TKT-20260915-0002 — `cache-first` KEPT, poll ADDED. 48 files reach this
    hook and it feeds useCapability's active-role decision, so it runs on
    effectively every screen; `cache-and-network` would add a request per
    navigation for a row that changes when someone is promoted. But with NO
    poll (the previous state) a role change never reached an open tab at all,
    and `isAdmin` is derived from it — the stale direction that matters is a
    DEMOTION the tab keeps honouring. 10 minutes instead of never.
  */
  const { data, loading } = useQuery(GetUserRoleForPropertyDocument, {
    variables: {
      user_id: userId!,
      property_id: selectedPropertyId!,
    },
    skip,
    fetchPolicy: FETCH_POLICIES.SEMI_STATIC,
    pollInterval: POLL_INTERVALS.SEMI_STATIC,
  })

  if (roleOverride) {
    return { roleKey: roleOverride, isAdmin: ADMIN_ROLES.has(roleOverride), loading: false }
  }

  const roleKey = data?.property_members?.[0]?.role_key ?? null

  // Wave 5c2: the null-role_key fail-open is CEILING-GATED. tenant_admin /
  // owner legitimately have no property_members row (tenant-scoped roles), so
  // for them a missing role_key still means admin. A 'staff' JWT with no
  // role_key must NOT be admin — pre-collapse the operational default_role
  // made this case impossible; post-collapse it is the difference between a
  // capability-gated user and an accidental admin.
  const jwtRole = user?.defaultRole ?? null
  const jwtIsCeiling = jwtRole === 'owner' || jwtRole === 'tenant_admin' || jwtRole === 'admin'

  return {
    roleKey,
    isAdmin: roleKey ? ADMIN_ROLES.has(roleKey) : jwtIsCeiling,
    loading: skip ? false : loading,
  }
}
